import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import { Button, notification, Popconfirm, Space, Table } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import moment from "moment";
import { getSupportState } from "../../redux/store/getState";
import { RequestAtc } from "../../redux/apiSlice/requestSlice";
import useSearch from "../../hooks/useSearch";
import { getSupportTickets } from "../../redux/apiActions/supportAction";

import { useNavigate } from "react-router-dom";
import handleApiError from "../../utils/handleError";
import axiosClient from "../../api/axiosClient";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const SupportTable: React.FC = () => {
  const { supportTickets, loading } = useAppSelector(getSupportState);
  const dispatch = useAppDispatch();
  const [data, setData] = useState<RequestAtc[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const navigate = useNavigate();
  useSearch(supportTickets, setData);

  const [closeLoading, setCloseLoading] = useState(false);

  useEffect(() => {
    dispatch(getSupportTickets());
  }, [dispatch]);

  async function handleClose(id: any) {
    setCloseLoading(true);
    try {
      await axiosClient.post(`support/ticket/${id}/transition`, {
        status: "closed",
      });
      dispatch(getSupportTickets());
      notification.success({
        message: "Closed",
        description: "Ticket closed successfully",
      });
    } catch (error) {
      handleApiError(error);
    } finally {
      setCloseLoading(false);
    }
  }

  const columns: ColumnsType<RequestAtc> = [
    {
      title: "Date And Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
      width: "15%",
    },

    {
      title: "Ticket ID",
      dataIndex: "ticketId",
      width: "15%",
    },
    {
      title: "Title",
      dataIndex: "title",
      width: "20%",
    },
    {
      title: "Message",
      dataIndex: "message",
      width: "50%",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "none",
              fontWeight: 500,
            }}
            type="primary"
            onClick={() => navigate(`/support/${record.id}`)}
          >
            Comment
          </Button>
          <Popconfirm
            title="Close the ticket"
            description="Are you sure to close the ticket?"
            onConfirm={() => handleClose(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" disabled={record.status === "closed"}>
              Close
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      style={{ marginTop: 20 }}
      loading={loading.getSupport || closeLoading}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      onChange={(p) =>
        setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
      }
      pagination={pagination}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default SupportTable;
